import type { Project } from '../data/projectTypes'
import { isCodingAgentConnected, projectAgentPrompt } from '../lib/agentConnection'
import { useGetProjectQuery } from '../services/projectDataApi'
import { CheckCircle2, CircleDashed } from 'lucide-react'
import './TestingSetup.css'
import './SetupPage.css'
import './chat/chat.css'
import { AgentPrompt } from './AgentPrompt'
import ApiTokenControls from './ApiTokenControls'

export default function AgentConnectionSetup({ project }: { project: Project }) {
  const query = useGetProjectQuery(project.id)
  const connected = isCodingAgentConnected(query.currentData ?? project)
  const projectUrl = `${window.location.origin}/projects/${project.id}/overview`
  return <div className="agent-connection testing-settings setup-stack">
    <section className="setup-section" aria-labelledby="agent-setup-title">
      <header className="setup-section__header">
        <span className={`setup-step${connected ? ' setup-step--done' : ''}`} aria-hidden>1</span>
        <div>
          <h2 id="agent-setup-title">Paste this prompt into your coding agent</h2>
          <p>Open the codebase for <a href={projectUrl}>{project.name}</a> in Cursor, Claude Code, or another agent. The prompt tells it how to review and fix the bugs QA reports.</p>
        </div>
      </header>
      <div className="setup-section__body">
        <AgentPrompt analyticsEvent="support-agent-prompt-copy" prompt={projectAgentPrompt(projectUrl)} />
      </div>
    </section>
    <section className="setup-section" aria-labelledby="agent-token-title">
      <header className="setup-section__header">
        <span className={`setup-step${connected ? ' setup-step--done' : ''}`} aria-hidden>2</span>
        <div>
          <h2 id="agent-token-title">Give your agent an API token</h2>
          <p>Create a token, copy it when it’s shown, and give it to your agent when asked. Tokens belong to your account and work across every project you can access.</p>
        </div>
      </header>
      <div className="setup-section__body agent-connection__tokens"><ApiTokenControls /></div>
    </section>
    <section className="setup-section" aria-labelledby="agent-status-title">
      <header className="setup-section__header">
        <span className={`setup-step${connected ? ' setup-step--done' : ''}`} aria-hidden>3</span>
        <div>
          <h2 id="agent-status-title">Confirm the connection</h2>
          <p>Once your agent has the prompt and a token, check the connection here.</p>
        </div>
      </header>
      <div className="setup-section__body">
        <div className={`setup-status ${connected ? 'setup-status--success' : 'setup-status--waiting'}`}>
          {connected ? <CheckCircle2 size={16} aria-hidden /> : <CircleDashed size={16} aria-hidden />}
          <strong role="status">{connected ? 'Agent connected' : 'Agent not connected'}</strong>
          {!connected && <span className="setup-status__detail">Your agent must read this project’s bugs list to be marked as connected, even if the list is empty.</span>}
          <button type="button" disabled={query.isFetching} onClick={() => void query.refetch()}>{query.isFetching ? 'Checking…' : 'Check now'}</button>
        </div>
        {query.isError && <p role="alert">Could not check the agent connection. Please try again.</p>}
      </div>
    </section>
  </div>
}
